import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  inject,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ToastService } from "../../services/toast.service";
import { DownloadService } from "../../services/download.service";

/**
 * 通用二维码分享弹窗
 *
 * 传入要生成二维码的 url，弹窗内完成二维码渲染、链接复制、二维码下载。
 * 用于：成绩分享二维码、新生登记二维码等。
 */
@Component({
  selector: "app-share-qr-dialog",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./share-qr-dialog.component.html",
  styleUrls: ["./share-qr-dialog.component.scss"],
})
export class ShareQrDialogComponent implements OnInit {
  /** 二维码内容链接 */
  @Input() url = "";
  /** 弹窗标题 */
  @Input() title = "二维码";
  /** 二维码下方提示文字 */
  @Input() hint = "";
  /** 下载文件名（不含 .png） */
  @Input() downloadName = "二维码";
  /** 点击关闭/遮罩时触发 */
  @Output() closed = new EventEmitter<void>();

  private readonly toast = inject(ToastService);
  private readonly downloadService = inject(DownloadService);

  readonly dataUrl = signal("");
  readonly generating = signal(true);
  readonly error = signal("");

  ngOnInit(): void {
    if (!this.url) {
      this.error.set("链接为空");
      this.generating.set(false);
      return;
    }
    // 懒加载 qrcode（独立 chunk）
    import("qrcode")
      .then((mod) => {
        // esbuild 在 production 分包（code-splitting）时会把 CommonJS 的 qrcode
        // 包成 `default` 导出；而 dev（未分包）时是命名导出。这里兼容两种情况。
        const lib = ((mod as any).default ?? mod) as typeof mod;
        return lib.toDataURL(this.url, {
          errorCorrectionLevel: "M",
          margin: 2,
          width: 240,
          color: { dark: "#1f2d3d", light: "#ffffff" },
        });
      })
      .then((dataUrl) => {
        this.dataUrl.set(dataUrl);
        this.generating.set(false);
      })
      .catch(() => {
        this.error.set("二维码生成失败，请重试");
        this.generating.set(false);
      });
  }

  close(): void {
    this.closed.emit();
  }

  copyLink(): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(this.url)
        .then(() => this.toast.success("链接已复制"))
        .catch(() => this.fallbackCopy());
    } else {
      this.fallbackCopy();
    }
  }

  private fallbackCopy(): void {
    const textarea = document.createElement("textarea");
    textarea.value = this.url;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "-9999px";
    textarea.contentEditable = "true";
    textarea.readOnly = true;
    document.body.appendChild(textarea);
    const range = document.createRange();
    range.selectNodeContents(textarea);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    textarea.setSelectionRange(0, this.url.length);
    try {
      document.execCommand("copy");
      this.toast.success("链接已复制");
    } catch {
      this.toast.info("请长按链接复制");
    }
    document.body.removeChild(textarea);
    if (selection) selection.removeAllRanges();
  }

  /** App 环境（Cordova）：模板据此区分「分享/保存到相册」与浏览器「下载」 */
  readonly isApp = this.downloadService.isApp;

  /** 浏览器/兜底：直接下载为文件 */
  downloadQr(): void {
    const dataUrl = this.dataUrl();
    if (!dataUrl) return;
    const fileName = `${this.downloadName}.png`;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    this.toast.success("二维码已下载");
  }

  /** App：调起系统分享面板；调不起时回退保存到相册（ADR-010） */
  shareImage(): void {
    const dataUrl = this.dataUrl();
    if (!dataUrl) return;
    const saver = (window as any).ImageSaver;
    if (!saver?.shareImage) {
      this.saveToAlbum();
      return;
    }
    saver.shareImage(
      dataUrl,
      `${this.downloadName}.png`,
      () => this.toast.info("已调起系统分享，请选择保存方式"),
      () => {
        // 分享面板无法调起（无分享目标/异常）→ 按 Q1=D 回退保存相册
        this.toast.info("分享不可用，已改为保存到相册");
        this.saveToAlbum();
      },
    );
  }

  /** App：直接保存到系统相册 */
  saveToAlbum(): void {
    const dataUrl = this.dataUrl();
    if (!dataUrl) return;
    const saver = (window as any).ImageSaver;
    if (!saver?.saveToAlbum) {
      this.toast.info("当前环境不支持保存到相册，可截图保存");
      return;
    }
    saver.saveToAlbum(
      dataUrl,
      `${this.downloadName}.png`,
      () => this.toast.success("已保存到相册"),
      (err: string) => {
        console.warn("saveToAlbum 失败", err);
        this.toast.info("保存失败，可截图保存");
      },
    );
  }
}
